import React from "react";
import { Link } from "react-router-dom";
import { LandingPageLayout } from "../shared/layout";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Reveal } from "@/components/motion";
import { useTranslation } from "react-i18next";
import { Shield, Heart, Lock, ArrowRight } from "lucide-react";

const principleIcons = [Heart, Shield, Lock];

export function AboutPageScreen() {
  const { t } = useTranslation();
  const principles = t("landing.about.principles.items", { returnObjects: true });

  return (
    <LandingPageLayout>
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 -z-10 bg-[linear-gradient(180deg,hsl(var(--accent)/0.7),hsl(var(--background)))]" />
        <div className="container mx-auto px-4 py-20 md:py-28 max-w-3xl text-center">
          <Reveal>
            <Badge variant="secondary" className="mb-5 rounded-full border border-primary/15">{t("landing.about.hero.badge")}</Badge>
            <h1 className="text-4xl md:text-5xl font-semibold tracking-tight leading-[1.1] mb-6">
              {t("landing.about.hero.title")}
            </h1>
            <p className="text-lg text-muted-foreground leading-relaxed">
              {t("landing.about.hero.description")}
            </p>
          </Reveal>
        </div>
      </section>

      <section className="py-16 md:py-20 border-y bg-secondary/45">
        <div className="container mx-auto px-4 max-w-4xl">
          <Reveal>
            <div className="border bg-card rounded-lg p-7 md:p-9">
            <h2 className="text-2xl md:text-3xl font-semibold mb-5">{t("landing.about.why.title")}</h2>
            <div className="space-y-4 text-muted-foreground leading-relaxed max-w-3xl">
              <p>{t("landing.about.why.body1")}</p>
              <p>{t("landing.about.why.body2")}</p>
              <p>{t("landing.about.why.body3")}</p>
            </div>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="py-20">
        <div className="container mx-auto px-4">
          <Reveal className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-semibold mb-3">{t("landing.about.principles.title")}</h2>
            <p className="text-muted-foreground max-w-xl mx-auto">
              {t("landing.about.principles.description")}
            </p>
          </Reveal>
          <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {principles.map((p, i) => {
              const Icon = principleIcons[i];
              return (
              <Reveal key={p.title} delay={i * 0.1}>
                <div className="rounded-lg border bg-card p-6 h-full">
                  <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
                    <Icon className="h-6 w-6 text-primary" />
                  </div>
                  <h3 className="font-medium text-lg mb-2">{p.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{p.text}</p>
                </div>
              </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-20 bg-primary text-primary-foreground">
        <div className="container mx-auto px-4 text-center max-w-2xl">
          <Reveal>
            <h2 className="text-3xl font-semibold mb-4">{t("landing.about.cta.title")}</h2>
            <p className="opacity-90 mb-8">{t("landing.about.cta.description")}</p>
            <Button size="lg" variant="secondary" className="h-12 px-8 text-base text-primary" asChild>
              <Link to="/register">{t("landing.about.cta.button")} <ArrowRight className="ml-2 h-4 w-4" /></Link>
            </Button>
          </Reveal>
        </div>
      </section>
    </LandingPageLayout>
  );
}
